import {
  CountryCode,
  isEUCountry,
} from '../types/CountryCode/CountryCode.types';
import { IBANForm } from '../../components/AddPayoutAccountIBANForm/AddPayoutAccountIBANForm.types';
import { UKSortCodeBankForm } from '../../components/AddPayoutAccountUKSortCodeForm/AddPayoutAccountUKSortCodeFormProps.types';

enum LegalType {
  BUSINESS = 'BUSINESS',
  PRIVATE = 'PRIVATE',
}

enum FORM_ID {
  IBAN = 'iban',
  SORT_CODE = 'SORT_CODE',
  ACH = 'ach',
  IDENTIFIER = 'bank_and_currency_identifier',
  VERIFICATION = 'verification',
}

const isNonEmpty = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

const hasNoEmptyFields = ({
  form,
  opts,
}: {
  form: Record<string, string>;
  opts?: { skipFields: string[] };
}): boolean =>
  Object.entries(form).every(([key, value]) => {
    const skipFields = opts?.skipFields ?? [];
    if (skipFields.includes(key)) return true;
    return isNonEmpty(value);
  });

const isLegalType = (v: unknown): v is LegalType =>
  v === 'BUSINESS' || v === 'PRIVATE';

//regex patterns from WISE API
const accountHolderNamePatternFromApi = new RegExp(
  '^[0-9A-Za-zÀ-ÖØ-öø-ÿĀ-ž-_()\'’*,.%#^@&/\\-?:{}~<>+$"\\[\\]\\\\ ]+$',
);
const BICPatternFromApi = new RegExp(
  '^[A-Za-z]{6}[A-Za-z\\d]{2}([A-Za-z\\d]{3})?$',
);
const IBANPatternFromApi = new RegExp('^[a-zA-Z]{2}[a-zA-Z0-9 ]{12,40}$');
const sortCodePatternFromApi = new RegExp('^\\d{2}-?\\d{2}-?\\d{2}$');
const ukAccountNumberPatternFromApi = new RegExp('[0-9]{8}');
const addressFieldPatternFromApi = new RegExp('^.{1,255}$');
const addressValidationOptions = {
  regexPattern: addressFieldPatternFromApi,
  minLength: 1,
  maxLength: 255,
} as const;

type BankForm = IBANForm | UKSortCodeBankForm;
type FieldError<T extends BankForm> = {
  field: keyof T;
  message: string;
};

const fieldValidator = (
  value: string,
  opts?: {
    regexPattern?: RegExp;
    minLength?: number;
    maxLength?: number;
  },
): boolean => {
  const trimmedValue = value.trim();
  if (opts?.regexPattern && !opts.regexPattern.test(trimmedValue)) {
    return false;
  }
  if (opts?.minLength && trimmedValue.length < opts.minLength) {
    return false;
  }
  if (opts?.maxLength && trimmedValue.length > opts.maxLength) {
    return false;
  }
  return true;
};

type AddressField = 'city' | 'addressLine' | 'postalCode';
const addressFieldLabels: Record<AddressField, string> = {
  city: 'city',
  addressLine: 'address',
  postalCode: 'postal code',
};

const validateAddressFields = <T extends BankForm>(
  form: T,
  addError: (field: keyof T, message: string) => void,
): void => {
  (Object.keys(addressFieldLabels) as AddressField[]).forEach((field) => {
    if (!fieldValidator(form[field], addressValidationOptions)) {
      addError(field as keyof T, `Invalid ${addressFieldLabels[field]}`);
    }
  });
};

const ibanFormValidator = (
  form: IBANForm,
  uk: boolean = false,
): { isValid: boolean; errors: FieldError<IBANForm>[] } => {
  const errors: FieldError<IBANForm>[] = [];

  const addError = (field: keyof IBANForm, message: string) => {
    errors.push({ field, message });
  };

  // Required fields (non-empty)
  (Object.keys(form) as Array<keyof IBANForm>).forEach((key) => {
    if (key === 'bic') return;
    if (!isNonEmpty(form[key])) addError(key, 'This field is required');
  });

  // Field-level validations
  if (
    !fieldValidator(form.accountHolderName, {
      regexPattern: accountHolderNamePatternFromApi,
      minLength: 2,
      maxLength: 140,
    })
  ) {
    addError('accountHolderName', 'Invalid account holder name');
  }

  const bic = form?.bic?.trim();
  if (
    bic &&
    !fieldValidator(bic, {
      regexPattern: BICPatternFromApi,
      minLength: 8,
      maxLength: 11,
    })
  ) {
    addError('bic', 'Invalid BIC');
  }

  if (
    !fieldValidator(form.iban, {
      regexPattern: IBANPatternFromApi,
      minLength: 14,
      maxLength: 42,
    })
  ) {
    addError('iban', 'Invalid IBAN');
  }

  validateAddressFields(form, addError);

  //iban can be submitted for UK bank as well as EU
  const validCountry = uk
    ? form.countryCode === CountryCode.UK
    : isEUCountry(form.countryCode) ||
      form.countryCode === 'CH' ||
      form.countryCode === 'LI';

  if (!validCountry) {
    addError(
      'countryCode',
      uk ? 'Country must be UK' : 'Country must be in EU, CH, or LI',
    );
  }

  if (!isLegalType(form.legalType)) {
    addError('legalType', 'Invalid legal type');
  }

  return { isValid: errors.length === 0, errors };
};

const ukSortCodeBankFormValidator = (
  form: UKSortCodeBankForm,
): { isValid: boolean; errors: FieldError<UKSortCodeBankForm>[] } => {
  const errors: FieldError<UKSortCodeBankForm>[] = [];

  const addError = (field: keyof UKSortCodeBankForm, message: string) => {
    errors.push({ field, message });
  };
  // Required fields (non-empty)
  (Object.keys(form) as Array<keyof UKSortCodeBankForm>).forEach((key) => {
    if (!isNonEmpty(form[key])) addError(key, 'This field is required');
  });

  if (
    !fieldValidator(form.accountHolderName, {
      regexPattern: accountHolderNamePatternFromApi,
      minLength: 2,
      maxLength: 140,
    })
  ) {
    addError('accountHolderName', 'Invalid account holder name');
  }

  //there is a separate api end point to validate sort code: https://api.sandbox.transferwise.tech/v1/validators/sort-code
  if (
    !fieldValidator(form.sortCode, {
      regexPattern: sortCodePatternFromApi,
      minLength: 6,
      maxLength: 8,
    })
  ) {
    addError('sortCode', 'Invalid sort code');
  }

  if (
    !fieldValidator(form.accountNumber, {
      regexPattern: ukAccountNumberPatternFromApi,
      minLength: 8,
      maxLength: 8,
    })
  ) {
    addError('accountNumber', 'Invalid account number');
  }

  if (form.countryCode !== CountryCode.UK) {
    addError('countryCode', 'Country must be UK');
  }

  validateAddressFields(form, addError);

  if (!isLegalType(form.legalType)) {
    addError('legalType', 'Invalid legal type');
  }

  return { isValid: errors.length === 0, errors };
};

export {
  ibanFormValidator,
  ukSortCodeBankFormValidator,
  hasNoEmptyFields,
  FORM_ID,
  LegalType,
};
